import React, { useContext, useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import {
    Box,
    IconButton,
    LinearProgress,
    Typography,
} from '@mui/material';
import {
    CheckBox,
    CheckBoxOutlineBlank,
    CheckCircleOutline,
    CancelOutlined,
    CloudSync,
    ErrorOutlineOutlined,
    OpenInNew,
} from '@mui/icons-material';
import {
    useGridApiRef,
    DataGridPro,
    LicenseInfo,
} from '@mui/x-data-grid-pro';

import { HasPermissions } from '../../contexts/PermissionContext';
import BaseCheckbox from '../checkbox/BaseCheckbox';
import MASVTransferProgress from '../progress/MASVTransferProgress';


LicenseInfo.setLicenseKey('17ca2e8476fa658618680f9f135dbc81T1JERVI6MzU2MTYsRVhQSVJZPTE4NzMzODQyMDIwMDAsS0VZVkVSU0lPTj0x',);


const UPLOADER_STATES = {
    // MASV Upload States
    new: { name: 'Ready', status_color: 'rgb(79, 195, 247)' },
    pending: { name: 'Transferring', status_color: 'rgb(79, 195, 247)', progress_color: 'rgb(79, 195, 247)' },
    completed: { name: 'Complete', status_color: 'rgb(0, 230, 118)', progress_color: 'rgb(0, 230, 118)' },
    cancelled: { name: 'Cancelled', status_color: 'rgb(158, 158, 158)', progress_color: 'rgb(158, 158, 158)' },
    error: { name: 'Error', status_color: 'rgb(239, 83, 80)', progress_color: 'rgb(239, 83, 80)' },
    // Konsol Custom States
    finalizing: { name: 'Finalizing', status_color: 'rgb(255, 213, 79)', progress_color: 'rgb(255, 213, 79)' },
}

const renderEditorialCheckbox = (params, handleEditorialCheck) => {
    return (
        <BaseCheckbox
            disabled={!HasPermissions('verify:media')}
            checked={params.value}
            onChange={(e) => {
                e.stopPropagation();
                handleEditorialCheck(params.row.transfer_date, !params.value)
            }}
        />
    );
};

const renderDetailCell = (params, handleTransferDateDetail) => {
    return (
        <IconButton
                variant="contained"
                sx={{
                    color: 'rgba(255, 255, 255, 1)',
                    '&:hover': {
                        color: 'rgb(76, 146, 107)',
                    },
                }}
                onClick={() => {
                    handleTransferDateDetail(params.row);
                }}
            >
                <OpenInNew />
            </IconButton>
    );
};

const renderTransferProgress = (row, apiRef) => {
    return (
        <MASVTransferProgress
            key={row?.id}
            apiRef={apiRef}
            progressColor={UPLOADER_STATES?.[row?.transfer_status]?.progress_color ?? 'rgb(158, 158, 158)'}
            row={row}
        />
    );
};

const renderTransferStatus = (value) => {
    return (
        <Box
                display='flex'
                alignItems='center'
            >
                <Box
                    px={1}
                >
                    {(UPLOADER_STATES?.[value]?.name ?? '') && (
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            backgroundColor: UPLOADER_STATES?.[value]?.status_color ?? 'rgb(158, 158, 158)',
                            borderRadius: 1,
                        }}
                    />
                )}
                </Box>
                <Typography
                    fontSize='12px'
                >
                    {UPLOADER_STATES?.[value]?.name ?? ''}
                </Typography>
            </Box>
    )
}

const formatDateTime = (value) => {
    try{
        if(value === null) throw ""
        const date = new Date(value)
        return Intl.DateTimeFormat(undefined, {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            dateStyle: 'short',
            timeStyle: 'short',
        }).format(date)
    }
    catch(e) {
        return "-"
    }
};

export default function AssetOverviewDataGrid(props) {

    const {
        handleTransferDateDetail,
        handleEditorialCheck,
        retrieveAssetOverview,
        rows,
        rowsLoading,
        setTransferDate,
        transferEnabled,
    } = props
    const { isAuthenticated } = useAuth0();
    const apiRef = useGridApiRef();

    const assetColumns = [
        {field: 'transfer_date',        headerName: 'Transfer Date',        width: 250},
        {field: 'camera',               headerName: 'Cameras',              width: 180},
        {field: 'shot_count',           headerName: 'Total Shots',          width: 180},
        {field: 'duration',             headerName: 'Total Duration',       width: 240},
        {field: 'size',                 headerName: 'Total Size',           width: 110, valueFormatter: ({ value }) => { return value }}, //bytesToHumanReadable(value) }}, // Store bytes in database and use this to display
    ]

    // const editorialColumns = [
    //     {field: "signoff_time",         headerName: "Editorial Sign Off Time",  width: 200, valueFormatter: ({ value }) => formatDateTime(value)},
    //     {field: "editor",               headerName: "Editor",                   width: 150},
    //     {field: 'result',               headerName: 'Editorial Check',          width: 180, renderCell: (params) => renderEditorialCheckbox(params, handleEditorialCheck)},
    // ]

    const detailsColumns = [
        { field: 'details',     headerName: '', width: 10, renderCell: (params) => renderDetailCell(params, handleTransferDateDetail) }
    ]

    const masvColumns = [
        {field: 'transfer_progress',    headerName: 'Transfer Progress',    width: 200, sortable: false, renderCell:({ row }) => renderTransferProgress(row, apiRef)},
        {field: 'transfer_status',          headerName: 'Transfer Status',      width: 150, sortable: false, renderCell:({ value }) => renderTransferStatus(value)},
        {field: 'submitted_by',       headerName: 'Transferred By',       width: 150},
        {field: 'submitted_at',      headerName: 'Transfer Started At',      width: 150, valueFormatter: ({ value }) => formatDateTime(value)},
        {field: 'completed_at',      headerName: 'Transfer Completed At',      width: 150, valueFormatter: ({ value }) => formatDateTime(value)},
    ]

    const [trackerColumns, setTrackerColumns] = useState([...assetColumns,  ...detailsColumns]);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);

    useEffect(() => {
        if (transferEnabled) {
            setTrackerColumns([...assetColumns, ...masvColumns, ...detailsColumns]);
        }
    }, [transferEnabled]);

    useEffect(() => {
        isAuthenticated && (() => {
            retrieveAssetOverview()
        })();
    }, [isAuthenticated])


    const handleRowDoubleClick = (params, event) => {
        event.stopPropagation()
        handleTransferDateDetail(params.row);
    };

    const handleRowSelection = (newRowSelectionModel) => {
        setRowSelectionModel(
            newRowSelectionModel.length
            ? [newRowSelectionModel[newRowSelectionModel.length - 1]]
            : []
        );
        const selectedRowData = apiRef.current.getRow(newRowSelectionModel[newRowSelectionModel.length - 1]);
        setTransferDate(selectedRowData ? selectedRowData.transfer_date : null);

    }

    const bytesToHumanReadable = (bytes) => {
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
        if (bytes === 0) return '0 Bytes';
        const k = 1024
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
        return `${size} ${sizes[i]}`;
    }

    return (
        <DataGridPro
            sx={{
                backgroundColor: '#101010', // Change the background color of the data grid
                borderColor: '#122218',
                '& .MuiDataGrid-root': {
                    cursor: 'pointer', // Change the cursor to pointer when hovering over the data grid
                },
                '& .MuiDataGrid-cell:hover': {
                    color: 'rgb(76, 146, 107)',
                },
                '& .MuiDataGrid-columnSeparator': {
                    borderColor: 'green', // Make the separator green
                    borderWidth: '2px', // Set the thickness of the separator
                    visibility: 'visible', // Ensure the separator always visible
                    '&:hover': {
                        borderColor: 'rgb(76, 146, 107)', // Ensure the separator stays green on hover
                    },
                },
                '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#101010', // Change the column header background to black
                },
                '& .MuiDataGrid-columnHeader': {
                    fontSize: '14px', // Customize the font size of the column headers
                    color: 'white', // Change the column header color to white
                    fontWeight: 'bold', // Make the column header text bold
                    borderBottom: 'None', // Change the bottom line color of the column headers
                },
                '& .MuiDataGrid-row': {
                    border: 'none', // Remove the row separator lines
                },
                '& .MuiDataGrid-rowCount': {
                    color: 'white', // Change the background color of the selected row
                },
                '& .MuiDataGrid-selectedRowCount': {
                    color: 'white', // Change the font color of the selected row count
                },
                '& .MuiDataGrid-cell': {
                    border: 'none', // Remove the cell separator lines
                    color: 'white', // Change the font color of the cells
                    fontSize: '13px', // Make the cell text smaller
                    userSelect: 'none'
                },
                '& .MuiDataGrid-row:nth-of-type(odd)': {
                    backgroundColor: '#161618', // Change the background color of odd rows
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                    backgroundColor: '#202020', // Change the background color of even rows
                },
                '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(76, 146, 107, 0.1)', // Change the hover color of the row to light green with reduced opacity
                },
                '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
                    color: 'white', // Change the color of the checkbox
                    transform: 'scale(0.7)', // Make the checkbox smaller
                    '& .MuiSvgIcon-root': {
                        borderRadius: '50%', // Make the checkbox a circle
                    },
                    '&.Mui-checked': {
                        color: '#54a277', // Change the color of the selected checkbox to green
                    },
                },
                '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
                    color: 'white', // Change the color of the checkbox in the column header
                    transform: 'scale(0.8)', // Make the checkbox smaller in the column header
                    '& .MuiSvgIcon-root': {
                        borderRadius: '50%', // Make the checkbox a circle in the column header
                    },
                    '&.Mui-checked': {
                        color: '#54a277', // Change the color of the selected checkbox to green in the column header
                    },
                },
            }}
            components={{
                LoadingOverlay: LinearProgress,
            }}
            apiRef={apiRef}
            columns={trackerColumns}
            loading={rowsLoading}
            onRowDoubleClick={handleRowDoubleClick}
            onRowSelectionModelChange={handleRowSelection}
            pageSize={4}
            rows={rows}
            rowSelectionModel={rowSelectionModel}
            checkboxSelection
        />
    );
}
